import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Activity, Send, ArrowLeft, Phone, Video } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function PatientChat() {
  const navigate = useNavigate();
  const [clinics, setClinics] = useState([]);
  const [clinicId, setClinicId] = useState("");
  const [name, setName] = useState("Priya Verma");
  const [phone, setPhone] = useState("+919811000010");
  const [started, setStarted] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [triage, setTriage] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    // Use the public clinics endpoint so chat works without login
    api.get("/public/clinics").then(r => {
      const active = (r.data || []).filter(c => c.is_active);
      setClinics(active);
      if (active[0]) setClinicId(active[0].id);
    });
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const start = () => {
    if (!clinicId || !name) return toast.error("Pick a clinic and enter your name");
    setStarted(true);
    setMessages([{ sender: "agent", text: `Hi ${name.split(" ")[0]}! I'm Pulse, your clinic's care assistant. What symptoms are you experiencing today?` }]);
  };

  const send = async () => {
    if (!input.trim()) return;
    const text = input.trim();
    setInput("");
    setMessages(m => [...m, { sender: "patient", text }]);
    setBusy(true);
    try {
      const r = await api.post("/triage/message", {
        session_id: sessionId,
        clinic_id: clinicId,
        patient_name: name,
        patient_phone: phone,
        text,
      });
      setSessionId(r.data.session_id);
      setMessages(m => [...m, { sender: "agent", text: r.data.ai.reply }]);
      setTriage(r.data.ai);
      if (r.data.appointment) {
        toast.success("Appointment booked: " + new Date(r.data.appointment.scheduled_at).toLocaleString());
      }
    } catch {
      setMessages(m => [...m, { sender: "agent", text: "Sorry, I had trouble. Could you repeat that?" }]);
    } finally { setBusy(false); }
  };

  if (!started) {
    return (
      <div className="min-h-screen app-bg flex items-center justify-center p-6">
        <div className="w-full max-w-md solid-panel p-8 fade-up">
          <button onClick={() => navigate("/login")} className="text-slate-500 text-sm flex items-center gap-1 mb-4 hover:text-[#004a99]" data-testid="patient-back-login">
            <ArrowLeft className="h-4 w-4" /> Back to login
          </button>
          <div className="flex items-center gap-3 mb-5">
            <div className="h-12 w-12 rounded-2xl bg-[#004a99] flex items-center justify-center text-white">
              <Activity className="h-6 w-6" />
            </div>
            <div>
              <div className="tiny-label">Patient WhatsApp Triage</div>
              <h1 className="text-2xl font-heading font-bold">Start a chat with your clinic</h1>
            </div>
          </div>
          <label className="text-sm font-medium text-slate-700">Choose clinic</label>
          <select className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 bg-white" value={clinicId} onChange={(e) => setClinicId(e.target.value)} data-testid="patient-clinic-select">
            {clinics.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <label className="text-sm font-medium text-slate-700 mt-4 block">Your name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="patient-name-input" />
          <label className="text-sm font-medium text-slate-700 mt-4 block">WhatsApp number</label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1" data-testid="patient-phone-input" />
          <Button onClick={start} className="mt-6 w-full bg-[#f37021] hover:bg-[#d9621b] text-white h-11" data-testid="patient-start-chat-btn">
            Start triage chat
          </Button>
        </div>
      </div>
    );
  }

  const clinic = clinics.find(c => c.id === clinicId);

  return (
    <div className="min-h-screen app-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md h-[88vh] flex flex-col rounded-3xl overflow-hidden shadow-2xl border border-slate-200 bg-white">
        {/* WA Header */}
        <div className="bg-[#075E54] text-white px-4 py-3 flex items-center gap-3">
          <button onClick={() => setStarted(false)} className="p-1" data-testid="chat-back-btn"><ArrowLeft className="h-5 w-5" /></button>
          <div className="h-9 w-9 rounded-full bg-white/15 ring-1 ring-white/30 flex items-center justify-center overflow-hidden">
            {clinic?.logo_url ? <img src={clinic.logo_url} alt="" className="h-full w-full object-cover" /> : <Activity className="h-4 w-4" />}
          </div>
          <div className="flex-1">
            <div className="font-semibold leading-tight">{clinic?.name}</div>
            <div className="text-xs text-white/70 flex items-center gap-1.5"><span className="pulse-dot" /> Pulse-Agent online</div>
          </div>
          <Phone className="h-5 w-5 text-white/80" />
          <Video className="h-5 w-5 text-white/80" />
        </div>

        {/* Triage badge */}
        {triage && triage.triage_level !== "unknown" && (
          <div className={`px-4 py-2 text-xs font-semibold flex items-center justify-between ${
            triage.triage_level === "emergency" ? "bg-rose-50 text-rose-700" :
            triage.triage_level === "urgent" ? "bg-amber-50 text-amber-700" :
            "bg-emerald-50 text-emerald-700"}`} data-testid="triage-badge">
            <span>Level: {triage.triage_level.toUpperCase()}</span>
            <span className="truncate max-w-[60%]">{triage.recommendation}</span>
          </div>
        )}

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 wa-bg overflow-y-auto p-4 space-y-2">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.sender === "patient" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm shadow-sm ${
                m.sender === "patient" ? "bg-[#dcf8c6] text-slate-900 rounded-tr-none" : "bg-white text-slate-800 rounded-tl-none"}`}
                data-testid={`chat-msg-${i}`}>
                {m.text}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="bg-white text-slate-500 rounded-lg rounded-tl-none px-3 py-2 text-sm shadow-sm">typing…</div>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="bg-[#f0f0f0] p-2 flex items-center gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && send()}
                 placeholder="Type a message"
                 className="bg-white rounded-full px-4"
                 data-testid="chat-input" />
          <Button onClick={send} disabled={busy} className="rounded-full h-10 w-10 p-0 bg-[#075E54] hover:bg-[#054640]" data-testid="chat-send-btn">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
